/**
 * The single PDF report builder for Veylo AI.
 *
 * Every "Download as PDF" in the app produces the same eight-section business
 * report through this module, so branding, layout and pagination live in one
 * place. Charts are drawn from aggregated numbers, so a report never depends
 * on a chart being visible on screen.
 */
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import type { ReportBreakdown, ReportMetric, ReportTable } from "./report-data";
import { formatNumber } from "./report-data";

export type ReportNarrative = {
  observations: string[];
  recommendations: string[];
  conclusion: string;
};

export type ReportSpec = {
  title: string;
  datasetName: string;
  preparedFor: string;
  dateRange?: string;
  metrics: ReportMetric[];
  dataTable?: ReportTable;
  breakdowns: ReportBreakdown[];
  narrative: ReportNarrative | null;
  summaryTable?: ReportTable;
  /** Shown instead of the AI sections when the narrative could not be produced. */
  narrativeNote?: string;
};

// Brand palette (indigo → purple), matching the app's design tokens.
const INDIGO: [number, number, number] = [79, 70, 229];
const PURPLE: [number, number, number] = [124, 58, 237];
const INK: [number, number, number] = [30, 31, 46];
const MUTED: [number, number, number] = [110, 114, 133];
const LINE: [number, number, number] = [226, 228, 240];
const SLICE_COLORS: [number, number, number][] = [
  INDIGO, PURPLE, [14, 165, 233], [16, 185, 129], [245, 158, 11], [239, 68, 68],
  [99, 102, 241], [217, 70, 239], [6, 182, 212], [132, 204, 22],
];

const PAGE_MARGIN = 14;

class ReportDoc {
  doc: jsPDF;
  y: number;
  width: number;
  height: number;

  constructor() {
    this.doc = new jsPDF({ unit: "mm", format: "a4" });
    this.width = this.doc.internal.pageSize.getWidth();
    this.height = this.doc.internal.pageSize.getHeight();
    this.y = PAGE_MARGIN;
  }

  get contentWidth() {
    return this.width - PAGE_MARGIN * 2;
  }

  ensure(space: number) {
    if (this.y + space > this.height - 22) {
      this.doc.addPage();
      this.y = PAGE_MARGIN;
    }
  }

  heading(text: string) {
    this.y += 6;
    this.ensure(18);
    this.doc.setFont("helvetica", "bold");
    this.doc.setFontSize(13);
    this.doc.setTextColor(...INK);
    this.doc.text(text, PAGE_MARGIN, this.y);
    this.y += 2.5;
    this.doc.setDrawColor(...INDIGO);
    this.doc.setLineWidth(0.6);
    this.doc.line(PAGE_MARGIN, this.y, PAGE_MARGIN + 26, this.y);
    this.y += 6;
  }

  paragraph(text: string, color = INK, size = 10) {
    if (!text) return;
    this.doc.setFont("helvetica", "normal");
    this.doc.setFontSize(size);
    this.doc.setTextColor(...color);
    const lines = this.doc.splitTextToSize(text, this.contentWidth) as string[];
    lines.forEach((line) => {
      this.ensure(6);
      this.doc.text(line, PAGE_MARGIN, this.y);
      this.y += size * 0.52;
    });
    this.y += 2;
  }

  bullets(items: string[]) {
    items.filter(Boolean).forEach((item) => {
      this.doc.setFont("helvetica", "normal");
      this.doc.setFontSize(10);
      const lines = this.doc.splitTextToSize(item, this.contentWidth - 6) as string[];
      lines.forEach((line, i) => {
        this.ensure(6);
        this.doc.setTextColor(...(i === 0 ? INDIGO : INK));
        if (i === 0) this.doc.text("•", PAGE_MARGIN, this.y);
        this.doc.setTextColor(...INK);
        this.doc.text(line, PAGE_MARGIN + 5, this.y);
        this.y += 5.2;
      });
      this.y += 1.5;
    });
    this.y += 2;
  }

  table(table: ReportTable) {
    this.ensure(24);
    autoTable(this.doc, {
      startY: this.y,
      head: [table.columns],
      body: table.rows.map((r) => r.map((c) => String(c ?? ""))),
      margin: { left: PAGE_MARGIN, right: PAGE_MARGIN },
      styles: { fontSize: 8.5, cellPadding: 2, textColor: INK, lineColor: LINE, lineWidth: 0.1 },
      headStyles: { fillColor: INDIGO, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 8.5 },
      alternateRowStyles: { fillColor: [247, 247, 252] },
      theme: "grid",
    });
    const finalY = (this.doc as unknown as { lastAutoTable?: { finalY: number } }).lastAutoTable?.finalY;
    this.y = (finalY ?? this.y) + 5;
    if (table.note) {
      this.paragraph(table.note, MUTED, 8.5);
    }
  }
}

function drawMetricsRow(rd: ReportDoc, metrics: ReportMetric[]) {
  if (!metrics.length) return;
  const perRow = metrics.length <= 3 ? metrics.length : 3;
  const gap = 4;
  const boxWidth = (rd.contentWidth - gap * (perRow - 1)) / perRow;
  const boxHeight = 18;

  metrics.forEach((metric, i) => {
    const col = i % perRow;
    if (col === 0) {
      rd.ensure(boxHeight + 4);
      if (i > 0) rd.y += 0;
    }
    const x = PAGE_MARGIN + col * (boxWidth + gap);
    rd.doc.setFillColor(247, 247, 252);
    rd.doc.setDrawColor(...LINE);
    rd.doc.roundedRect(x, rd.y, boxWidth, boxHeight, 2, 2, "FD");

    rd.doc.setFont("helvetica", "bold");
    rd.doc.setFontSize(13);
    rd.doc.setTextColor(...INDIGO);
    rd.doc.text(String(metric.value), x + 4, rd.y + 8.5);

    rd.doc.setFont("helvetica", "normal");
    rd.doc.setFontSize(8);
    rd.doc.setTextColor(...MUTED);
    const label = rd.doc.splitTextToSize(metric.label, boxWidth - 8) as string[];
    rd.doc.text(label[0] ?? "", x + 4, rd.y + 14);

    if (col === perRow - 1 || i === metrics.length - 1) rd.y += boxHeight + gap;
  });
  rd.y += 2;
}

function drawBarChart(rd: ReportDoc, items: { name: string; value: number }[]) {
  const rowHeight = 7;
  const chartHeight = items.length * rowHeight + 4;
  rd.ensure(chartHeight);
  const labelWidth = 45;
  const barArea = rd.contentWidth - labelWidth - 22;
  const max = Math.max(...items.map((i) => i.value), 1);

  items.forEach((item, i) => {
    const y = rd.y + i * rowHeight;
    rd.doc.setFont("helvetica", "normal");
    rd.doc.setFontSize(8.5);
    rd.doc.setTextColor(...INK);
    const label = (rd.doc.splitTextToSize(item.name, labelWidth - 2) as string[])[0] ?? "";
    rd.doc.text(label, PAGE_MARGIN, y + 4);

    const width = Math.max((item.value / max) * barArea, 0.6);
    const color = SLICE_COLORS[i % SLICE_COLORS.length];
    rd.doc.setFillColor(...color);
    rd.doc.roundedRect(PAGE_MARGIN + labelWidth, y + 0.8, width, 4.4, 1, 1, "F");

    rd.doc.setTextColor(...MUTED);
    rd.doc.text(formatNumber(item.value), PAGE_MARGIN + labelWidth + width + 2, y + 4.3);
  });

  rd.y += chartHeight;
}

function drawPieChart(rd: ReportDoc, items: { name: string; value: number }[]) {
  const size = 46;
  rd.ensure(size + 6);
  const radius = size / 2;
  const cx = PAGE_MARGIN + radius;
  const cy = rd.y + radius;
  const total = items.reduce((a, b) => a + b.value, 0) || 1;

  let angle = -Math.PI / 2;
  items.forEach((item, i) => {
    const slice = (item.value / total) * Math.PI * 2;
    const color = SLICE_COLORS[i % SLICE_COLORS.length];
    rd.doc.setFillColor(...color);
    // Stroking in the same colour closes the hairline seams between segments.
    rd.doc.setDrawColor(...color);
    rd.doc.setLineWidth(0.3);

    // Approximate the slice with a triangle fan — reliable across viewers.
    const steps = Math.max(2, Math.ceil((slice / (Math.PI * 2)) * 60));
    for (let s = 0; s < steps; s++) {
      const a1 = angle + (slice * s) / steps;
      const a2 = angle + (slice * (s + 1)) / steps;
      rd.doc.triangle(
        cx, cy,
        cx + radius * Math.cos(a1), cy + radius * Math.sin(a1),
        cx + radius * Math.cos(a2), cy + radius * Math.sin(a2),
        "FD",
      );
    }
    angle += slice;
  });

  // Legend beside the pie.
  const legendX = PAGE_MARGIN + size + 8;
  items.forEach((item, i) => {
    const y = rd.y + 4 + i * 6;
    const color = SLICE_COLORS[i % SLICE_COLORS.length];
    rd.doc.setFillColor(...color);
    rd.doc.rect(legendX, y - 2.6, 3, 3, "F");
    rd.doc.setFont("helvetica", "normal");
    rd.doc.setFontSize(8.5);
    rd.doc.setTextColor(...INK);
    const pct = Math.round((item.value / total) * 100);
    const label = (rd.doc.splitTextToSize(
      `${item.name} — ${formatNumber(item.value)} (${pct}%)`,
      rd.contentWidth - size - 12,
    ) as string[])[0] ?? "";
    rd.doc.text(label, legendX + 5, y);
  });

  rd.y += Math.max(size, items.length * 6 + 6) + 4;
}

function drawCover(rd: ReportDoc, spec: ReportSpec) {
  rd.doc.setFillColor(...INDIGO);
  rd.doc.rect(0, 0, rd.width, 34, "F");
  rd.doc.setFillColor(...PURPLE);
  rd.doc.rect(0, 30, rd.width, 4, "F");

  rd.doc.setFont("helvetica", "bold");
  rd.doc.setFontSize(18);
  rd.doc.setTextColor(255, 255, 255);
  rd.doc.text(spec.title, PAGE_MARGIN, 16);

  rd.doc.setFont("helvetica", "normal");
  rd.doc.setFontSize(9.5);
  const subtitle = [spec.datasetName, spec.dateRange].filter(Boolean).join("  •  ");
  rd.doc.text(subtitle, PAGE_MARGIN, 24);

  rd.y = 44;
}

function drawFooters(rd: ReportDoc, spec: ReportSpec) {
  const pages = rd.doc.getNumberOfPages();
  const generated = new Date().toLocaleDateString();
  for (let p = 1; p <= pages; p++) {
    rd.doc.setPage(p);
    const y = rd.height - 12;
    rd.doc.setDrawColor(...LINE);
    rd.doc.setLineWidth(0.2);
    rd.doc.line(PAGE_MARGIN, y - 4, rd.width - PAGE_MARGIN, y - 4);

    rd.doc.setFont("helvetica", "normal");
    rd.doc.setFontSize(7.5);
    rd.doc.setTextColor(...MUTED);
    const left = [spec.preparedFor, spec.title, spec.dateRange || generated]
      .filter(Boolean)
      .join("  |  ");
    // Clip so the left strip can never run into the centred CONFIDENTIAL mark.
    const maxLeft = rd.width / 2 - PAGE_MARGIN - 14;
    let leftText = left;
    while (leftText.length > 8 && rd.doc.getTextWidth(leftText) > maxLeft) {
      leftText = leftText.slice(0, -2);
    }
    if (leftText !== left) leftText = `${leftText.trimEnd()}…`;
    rd.doc.text(leftText, PAGE_MARGIN, y);

    rd.doc.setFont("helvetica", "bold");
    rd.doc.setTextColor(...PURPLE);
    rd.doc.text("CONFIDENTIAL", rd.width / 2, y, { align: "center" });

    rd.doc.setFont("helvetica", "normal");
    rd.doc.setTextColor(...MUTED);
    rd.doc.text(`Page ${p} of ${pages}`, rd.width - PAGE_MARGIN, y, { align: "right" });
  }
}

/** Builds the eight-section report and returns it as a PDF blob. */
export function buildReport(spec: ReportSpec): Blob {
  const rd = new ReportDoc();

  // 1. Header summary
  drawCover(rd, spec);
  drawMetricsRow(rd, spec.metrics);

  // 2. Data log
  if (spec.dataTable) {
    rd.heading(spec.dataTable.title || "Data log");
    rd.table(spec.dataTable);
  }

  // 3. Breakdowns (table + chart)
  spec.breakdowns.forEach((breakdown) => {
    rd.heading(breakdown.title);
    rd.table({
      columns: ["Group", "Value"],
      rows: breakdown.items.map((i) => [i.name, formatNumber(i.value)]),
    });
    if (breakdown.chart === "pie") drawPieChart(rd, breakdown.items);
    else drawBarChart(rd, breakdown.items);
  });

  // 4 & 5. Key observations and recommendations
  if (spec.narrative) {
    if (spec.narrative.observations.length) {
      rd.heading("Key observations");
      rd.bullets(spec.narrative.observations);
    }
    if (spec.narrative.recommendations.length) {
      rd.heading("Recommendations");
      rd.bullets(spec.narrative.recommendations);
    }
  } else if (spec.narrativeNote) {
    rd.heading("Key observations");
    rd.paragraph(spec.narrativeNote, MUTED);
  }

  // 6. Overall summary table
  if (spec.summaryTable) {
    rd.heading(spec.summaryTable.title || "Overall summary");
    rd.table(spec.summaryTable);
  }

  // 7. Conclusion
  if (spec.narrative?.conclusion) {
    rd.heading("Conclusion");
    rd.paragraph(spec.narrative.conclusion);
  }

  // 8. Footer on every page
  drawFooters(rd, spec);

  return rd.doc.output("blob");
}

export function downloadReport(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
